<template>
  <div>
    <page-header
      :title="$t('company_limits_title')"
      :subtitle="headerSubtitle"
      icon="mdi-shield-account"
      icon-color="primary"
    >
      <template #actions>
        <primary-button @click="createDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_new_limit") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar :searchable="false">
      <template #filters>
        <v-col cols="auto">
          <autocomplete
            v-model="selectedRestaurantFilter"
            :items="formattedRestaurants"
            item-value="id"
            item-text="name"
            :label="$t('restaurant')"
            hide-details
            color="white"
            clearable
            multiple
            select-all
            style="min-width: 200px"
          />
        </v-col>
        <v-col cols="auto">
          <text-field
            v-model="search"
            :label="$t('search_limits')"
            kind="search"
            hide-details
            color="white"
          />
        </v-col>
      </template>
    </page-toolbar>

    <div v-if="isLoading" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" size="64" />
    </div>

    <div v-else class="px-6 mt-2">
      <limits-data-table
        :items="limits"
        :headers="headers"
        :loading="isLoading"
        :restaurants="restaurants"
        :companies="[]"
        :can-edit="canEditLimit"
        @edit="editingLimit = $event; configDialog = true"
        @delete="deleteLimit($event)"
        @toggle="toggleLimitState($event)"
      >
        <template #no-data>
          <empty-state
            icon="mdi-shield-off"
            :title="$t('no_limits_defined')"
            :subtitle="$t('no_limits_subtitle')"
            class="ma-4"
          >
            <primary-button @click="createDialog = true">
              <v-icon left>mdi-plus-circle</v-icon>
              {{ $t("add_new_limit") }}
            </primary-button>
          </empty-state>
        </template>
      </limits-data-table>
    </div>

    <create-limit-dialog
      v-model="createDialog"
      :company-id="companyId"
      @created="handleLimitCreated"
    />

    <limit-configuration-dialog
      v-if="editingLimit"
      v-model="configDialog"
      :limit="editingLimit"
      :company-id="companyId"
      :readonly="!canEditLimit(editingLimit)"
      @saved="loadLimits"
    />
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import setLang from "@/mixins/setLang";
import hasLimitTable from "@/mixins/hasLimitTable";
import EmptyState from "@/components/global/EmptyState.vue";
import LimitsDataTable from "@/components/limits/LimitsDataTable.vue";
import limitService from "@/services/limit";
import companyService from "@/services/company";
import CreateLimitDialog from "@/components/limitConfiguration/dialogs/CreateLimitDialog.vue";
import LimitConfigurationDialog from "@/components/limitConfiguration/dialogs/LimitConfigurationDialog.vue";
import {
  normalizeLimitData,
  sortLimitsByPriority,
  getLimitScopeType,
} from "@/utils/limitHelpers";

export default {
  name: "CompanyLimitsPage",
  mixins: [hasSnackbar, setLang, hasLimitTable],
  components: {
    EmptyState,
    LimitsDataTable,
    CreateLimitDialog,
    LimitConfigurationDialog,
  },
  data() {
    return {
      isLoading: false,
      search: "",
      allLimits: [],
      restaurants: [],
      companyName: "",
      createDialog: false,
      configDialog: false,
      editingLimit: null,
      selectedRestaurantFilter: [],
    };
  },
  computed: {
    companyId() {
      return (
        this.$route.params.id ||
        this.$store.state.company?.id ||
        this.$store.state.user?.user?.company_id
      );
    },
    headerSubtitle() {
      if (!this.companyId) return "";
      return `${this.companyName} #${this.companyId}`;
    },
    formattedRestaurants() {
      return this.restaurants.map((r) => ({
        ...r,
        name: this.toLatin(r.name),
      }));
    },
    partnerRestaurantIds() {
      return this.restaurants.map((r) => Number(r.id)).filter((id) => !isNaN(id));
    },
    usedRestaurantIds() {
      return [
        ...new Set(
          this.allLimits
            .map((l) => Number(l.restaurant_id))
            .filter((id) => !isNaN(id) && id !== 0)
        ),
      ];
    },
    limits() {
      const currentCompanyId = Number(this.companyId);
      let result = this.allLimits.filter((limit) => {
        const limitCompanyId = Number(limit.company_id);
        const limitRestaurantId = Number(limit.restaurant_id);
        return (
          (!Number.isNaN(limitCompanyId) && limitCompanyId === currentCompanyId) ||
          (!Number.isNaN(limitRestaurantId) &&
            this.partnerRestaurantIds.includes(limitRestaurantId))
        );
      });

      if (this.search) {
        const q = this.search.toLowerCase();
        result = result.filter((l) => l.name?.toLowerCase().includes(q));
      }

      if (this.selectedRestaurantFilter.length > 0) {
        result = result.filter((l) =>
          this.selectedRestaurantFilter.includes(l.restaurant_id)
        );
      }

      return [...result].sort(sortLimitsByPriority);
    },
    headers() {
      if (!this.$t) return [];
      return [
        { text: "ID", value: "id", align: "start", width: "80px" },
        { text: this.$t("name"), value: "name", sortable: true },
        { text: this.$t("restaurant"), value: "restaurant_id", sortable: true },
        {
          text: this.$t("limit_type"),
          value: "params.limit_definition.active",
          sortable: true,
        },
        { text: this.$t("time_limit"), value: "params.timing.active", sortable: true },
        {
          text: this.$t("who_pays"),
          value: "params.payment_logic.active",
          sortable: true,
        },
        {
          text: this.$t("what_can_order"),
          value: "params.ordering_rules.active",
          sortable: true,
        },
        { text: this.$t("active"), value: "state", align: "center", sortable: true },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
          sortable: false,
        },
      ];
    },
  },
  watch: {
    companyId: {
      handler() {
        this.loadCompany();
      },
    },
  },
  mounted() {
    this.loadCompany();
    this.loadMeta();
  },
  methods: {
    canEditLimit(item) {
      return (
        getLimitScopeType(item) === "company_only" ||
        item.restaurant_id === this.$store.state.restaurant?.id
      );
    },
    async loadCompany() {
      if (!this.companyId) {
        this.companyName = "";
        return;
      }
      if (this.$store.state.company?.id == this.companyId) {
        this.companyName = this.$store.state.company?.name || "";
        return;
      }
      try {
        const { data } = await companyService.getCompanyById(this.companyId);
        this.companyName = data?.name || "";
      } catch (error) {
        // non-critical
      }
    },
    async loadMeta() {
      try {
        const response = await companyService.getCompanyRestaurants(this.companyId);
        this.restaurants = response?.data || [];
      } catch (error) {
        // non-critical
      }
      await this.loadLimits();
    },
    async loadLimits() {
      this.isLoading = true;
      try {
        const limitsData = await limitService.getAllLimits({});
        this.allLimits = limitsData.map((limit) => normalizeLimitData(limit));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_limits"));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
